import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  IconButton,
  Collapse,
  CircularProgress,
  Alert,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  HelpOutline as NoAssignmentIcon,
} from '@mui/icons-material';
import { useFeedTransitionSchedule } from '../hooks/useFeed';
import { useQueryClient } from '@tanstack/react-query';
import {
  FeedTransitionBatch,
  FeedTransitionStatus,
  FeedTransitionFormulaIngredient,
} from '../types';

// ─── helpers ────────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<
  FeedTransitionStatus,
  { label: string; color: 'success' | 'warning' | 'error' | 'default'; icon: React.ReactNode }
> = {
  on_track: {
    label: 'On Track',
    color: 'success',
    icon: <CheckCircleIcon fontSize="small" />,
  },
  due_soon: {
    label: 'Due Soon',
    color: 'warning',
    icon: <WarningIcon fontSize="small" />,
  },
  needs_transition: {
    label: 'Needs Transition',
    color: 'error',
    icon: <ErrorIcon fontSize="small" />,
  },
  no_assignment: {
    label: 'No Assignment',
    color: 'default',
    icon: <NoAssignmentIcon fontSize="small" />,
  },
};

// ─── mixing formula expand row ───────────────────────────────────────────────

const BRAND_LABELS: Record<string, string> = {
  layermax: 'Maridav LAYERMAX 5%',
  jubaili: 'Jubaili JLC 5%',
};

const BRAND_STAGES: Record<string, { label: string; weeks: string }[]> = {
  layermax: [
    { label: 'Chick Starter', weeks: 'Wk 1–6' },
    { label: 'Chick Grower', weeks: 'Wk 7–11' },
    { label: 'Pullet', weeks: 'Wk 12–15' },
    { label: 'Pre-Lay', weeks: 'Wk 16–19' },
    { label: 'Layer Mash', weeks: 'Wk 20+' },
  ],
  jubaili: [
    { label: 'Starter', weeks: 'Wk 1–6' },
    { label: 'Grower', weeks: 'Wk 7–15' },
    { label: 'Pre-Layer', weeks: 'Wk 16–19' },
    { label: 'Layer', weeks: 'Wk 20+' },
  ],
};

function FormulaRow({
  formula,
  brand,
}: {
  formula: FeedTransitionFormulaIngredient[];
  brand: string;
}) {
  return (
    <Box sx={{ p: 2 }}>
      <Typography
        variant="caption"
        sx={{ color: 'rgba(255,255,255,0.5)', mb: 1, display: 'block' }}
      >
        {BRAND_LABELS[brand] ?? brand} Mixing Formula (per 1,000 kg finished feed)
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {formula.map((item) => (
          <Paper
            key={item.ingredient}
            sx={{
              px: 1.5,
              py: 0.75,
              border: '1px solid rgba(255,152,0,0.2)',
              minWidth: 140,
            }}
          >
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'block' }}>
              {item.ingredient}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#ff9800' }}>
              {item.kg_per_tonne} kg
            </Typography>
          </Paper>
        ))}
      </Box>
    </Box>
  );
}

// ─── single batch row ────────────────────────────────────────────────────────

function BatchRow({ batch, brand }: { batch: FeedTransitionBatch; brand: string }) {
  const [open, setOpen] = useState(false);
  const { transition_status: status } = batch;
  const cfg = STATUS_CONFIG[status];

  const rowBg =
    status === 'needs_transition'
      ? 'rgba(244,67,54,0.06)'
      : status === 'due_soon'
        ? 'rgba(255,152,0,0.06)'
        : 'transparent';

  return (
    <>
      <TableRow sx={{ bgcolor: rowBg, '& td': { borderBottom: '1px solid rgba(255,152,0,0.08)' } }}>
        {/* expand */}
        <TableCell sx={{ width: 48, p: 1 }}>
          <IconButton size="small" onClick={() => setOpen((p) => !p)}>
            {open ? <CollapseIcon /> : <ExpandIcon />}
          </IconButton>
        </TableCell>

        {/* batch name */}
        <TableCell>
          <Typography variant="body2" fontWeight={600}>
            {batch.batch_name}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
            {batch.bird_count.toLocaleString()} birds
          </Typography>
        </TableCell>

        {/* age */}
        <TableCell>
          <Typography variant="body2">{batch.age_weeks} wks</Typography>
        </TableCell>

        {/* expected stage */}
        <TableCell>
          <Typography variant="body2" fontWeight={600} sx={{ color: '#ff9800' }}>
            {batch.current_stage.label}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
            Wk {batch.current_stage.min_week}
            {batch.current_stage.max_week < 999 ? `–${batch.current_stage.max_week}` : '+'}
          </Typography>
        </TableCell>

        {/* current feed */}
        <TableCell>
          {batch.current_assignment ? (
            <>
              <Typography variant="body2">{batch.current_assignment.feed_type_name}</Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                {batch.current_assignment.feed_brand_name}
              </Typography>
            </>
          ) : (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>
              —
            </Typography>
          )}
        </TableCell>

        {/* status chip */}
        <TableCell>
          <Chip
            size="small"
            label={cfg.label}
            color={cfg.color}
            icon={cfg.icon as React.ReactElement}
            sx={{ fontWeight: 600 }}
          />
        </TableCell>

        {/* next transition */}
        <TableCell>
          {batch.next_stage ? (
            <>
              <Typography variant="body2">{batch.next_stage.label}</Typography>
              <Typography
                variant="caption"
                sx={{
                  color:
                    (batch.next_stage.days_until_transition ?? 999) <= 7
                      ? '#ff9800'
                      : 'rgba(255,255,255,0.4)',
                }}
              >
                {batch.next_stage.days_until_transition != null
                  ? `in ${batch.next_stage.days_until_transition}d`
                  : `at wk ${batch.next_stage.min_week}`}
              </Typography>
            </>
          ) : (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>
              Final stage
            </Typography>
          )}
        </TableCell>
      </TableRow>

      {/* expanded: mixing formula */}
      <TableRow>
        <TableCell colSpan={7} sx={{ p: 0, border: 'none' }}>
          <Collapse in={open} unmountOnExit>
            <FormulaRow formula={batch.current_stage.mixing_formula} brand={brand} />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// ─── summary card ────────────────────────────────────────────────────────────

function SummaryCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <Paper
      sx={{
        p: 2,
        textAlign: 'center',
        border: `1px solid ${color}33`,
        flex: 1,
        minWidth: 120,
      }}
    >
      <Typography variant="h4" fontWeight={700} sx={{ color }}>
        {value}
      </Typography>
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
        {label}
      </Typography>
    </Paper>
  );
}

// ─── page ────────────────────────────────────────────────────────────────────

const FeedTransitionTracker: React.FC = () => {
  const [brand, setBrand] = useState<'layermax' | 'jubaili'>('layermax');
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useFeedTransitionSchedule(brand);

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['feed', 'transition-schedule', brand] });
  };

  const summary = data?.summary;
  const batches: FeedTransitionBatch[] = data?.batches ?? [];

  return (
    <Box sx={{ p: 3 }}>
      {/* header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Feed Transition Tracker
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mt: 0.5 }}>
            {BRAND_LABELS[brand]} mixing schedule — tracks when each batch should change feed type
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ToggleButtonGroup
            value={brand}
            exclusive
            size="small"
            onChange={(_, v) => v && setBrand(v)}
            sx={{
              '& .MuiToggleButton-root': {
                color: 'rgba(255,255,255,0.5)',
                borderColor: 'rgba(255,152,0,0.3)',
                fontSize: 12,
                px: 1.5,
                '&.Mui-selected': { color: '#ff9800', bgcolor: 'rgba(255,152,0,0.12)' },
              },
            }}
          >
            <ToggleButton value="layermax">LAYERMAX</ToggleButton>
            <ToggleButton value="jubaili">Jubaili JLC</ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} disabled={isLoading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* summary cards */}
      {summary && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <SummaryCard label="Total Batches" value={summary.total_batches} color="#ff9800" />
          <SummaryCard label="On Track" value={summary.on_track} color="#4caf50" />
          <SummaryCard label="Due Soon" value={summary.due_soon} color="#ff9800" />
          <SummaryCard label="Needs Transition" value={summary.needs_transition} color="#f44336" />
          <SummaryCard
            label="No Assignment"
            value={summary.no_assignment}
            color="rgba(255,255,255,0.3)"
          />
        </Box>
      )}

      {/* stage reference legend */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography
          variant="caption"
          sx={{ color: 'rgba(255,255,255,0.5)', mb: 1, display: 'block' }}
        >
          {BRAND_LABELS[brand]} Stage Reference
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {(BRAND_STAGES[brand] ?? []).map((s) => (
            <Box
              key={s.label}
              sx={{
                px: 1.5,
                py: 0.5,
                border: '1px solid rgba(255,152,0,0.3)',
                borderRadius: 1,
                display: 'flex',
                gap: 1,
                alignItems: 'center',
              }}
            >
              <Typography variant="body2" fontWeight={600} sx={{ color: '#ff9800' }}>
                {s.label}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                {s.weeks}
              </Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* loading / error */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress color="primary" />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load transition schedule. Please refresh.
        </Alert>
      )}

      {/* table */}
      {!isLoading && !error && (
        <Paper sx={{ overflow: 'hidden' }}>
          {batches.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.4)' }}>
                No active bird batches found.
              </Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'rgba(255,152,0,0.08)' }}>
                  <TableCell sx={{ width: 48 }} />
                  <TableCell sx={{ fontWeight: 700 }}>Batch</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Age</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Expected Stage</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Current Feed</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Next Transition</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {batches.map((batch) => (
                  <BatchRow key={batch.birds_id} batch={batch} brand={brand} />
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default FeedTransitionTracker;
